import type { ApiFailure, ApiSuccess, DeletionReference, EntityRecord, EntityType, Workspace } from "@wedding/schemas";

export class WeddingApiError extends Error {
  constructor(message: string, readonly code: string, readonly fieldErrors?: unknown) {
    super(message);
    this.name = "WeddingApiError";
  }
}

export function deletionReferences(error: unknown): DeletionReference[] {
  if (!(error instanceof WeddingApiError) || error.code !== "RELATION_IN_USE") return [];
  const details = error.fieldErrors;
  if (!details || typeof details !== "object" || !("references" in details)) return [];
  const references = (details as { references?: unknown }).references;
  return Array.isArray(references)
    ? references.filter((reference): reference is DeletionReference =>
        Boolean(reference) &&
        typeof reference === "object" &&
        typeof (reference as DeletionReference).entityType === "string" &&
        typeof (reference as DeletionReference).entityId === "string" &&
        typeof (reference as DeletionReference).moduleLabel === "string" &&
        typeof (reference as DeletionReference).recordLabel === "string")
    : [];
}

export class WeddingApi {
  constructor(private readonly baseUrl: string) {}
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, { credentials: "include", ...init, headers: { "content-type": "application/json", ...init?.headers } });
    const body = await response.json() as unknown;
    if (!response.ok || !body || typeof body !== "object" || !("success" in body) || body.success !== true) {
      const failure = body as ApiFailure;
      throw new WeddingApiError(failure.error?.message ?? "请求失败", failure.error?.code ?? "REQUEST_FAILED", failure.error?.fieldErrors);
    }
    return body as T;
  }
  workspaces() { return this.request<ApiSuccess<Workspace[]>>("/workspaces"); }
  workspace(id: string) { return this.request<ApiSuccess<Workspace>>(`/workspaces/${id}`); }
  dashboard(id: string) { return this.request<ApiSuccess<Record<string, unknown>>>(`/workspaces/${id}/dashboard`); }
  async list(id: string, type: EntityType, query = "?limit=500") {
    const parameters = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
    if (!parameters.has("limit")) parameters.set("limit", "500");
    const data: EntityRecord[] = [];
    const seenCursors = new Set<string>();
    let result: ApiSuccess<EntityRecord[]>;
    let cursor: string | undefined;
    do {
      if (cursor) parameters.set("cursor", cursor);
      else parameters.delete("cursor");
      result = await this.request<ApiSuccess<EntityRecord[]>>(
        `/workspaces/${id}/${type}?${parameters.toString()}`,
      );
      data.push(...result.data);
      cursor = result.meta.cursor;
      if (cursor) {
        if (seenCursors.has(cursor))
          throw new Error("列表分页游标重复，已停止加载以避免数据异常");
        seenCursors.add(cursor);
      }
    } while (cursor);
    return { ...result, data };
  }
  create(id: string, type: EntityType, data: Record<string, unknown>) { return this.request<ApiSuccess<EntityRecord>>(`/workspaces/${id}/${type}`, { method: "POST", body: JSON.stringify({ data }) }); }
  update(id: string, type: EntityType, entityId: string, data: Record<string, unknown>, expectedVersion: number) { return this.request<ApiSuccess<EntityRecord>>(`/workspaces/${id}/${type}/${entityId}`, { method: "PATCH", body: JSON.stringify({ data, expectedVersion }) }); }
  remove(id: string, type: EntityType, entityId: string, expectedVersion: number, cascade = false) { return this.request<ApiSuccess<EntityRecord>>(`/workspaces/${id}/${type}/${entityId}`, { method: "DELETE", body: JSON.stringify({ expectedVersion, cascade }) }); }
}
